/**
 * Football 101 progress (docs/TUTORIAL.md §3.9), persisted to localStorage `hf.tutorial.v1` with the same
 * never-throw pattern as settings.ts. A signal so the hub, the main menu badge and the first-launch offer update
 * live when the engine saves a lesson.
 */
import { signal } from '@preact/signals-core';
import { CHARACTER_IDS, type CharacterId } from '@hf/shared';
import { getPlatform, type KeyValueStorage } from '../../platform';
import { cloneJson } from '../clone';
import { LESSONS, type LessonId } from './catalog';

export const TUTORIAL_KEY = 'hf.tutorial.v1';

export interface LessonRecord {
  /**
   * done ✓ · skipped ↷ · played (legacy: a Game Day lost or tied before playtest #4. Finishing Game Day is DONE now
   * — a lost Game Day kept a newcomer at 11/12 behind "WIN GAME DAY" — and loading turns `played` into done).
   */
  status: 'done' | 'skipped' | 'played';
  /** Epoch ms. */
  at: number;
  /** Plays snapped in the lesson's last run. */
  tries: number;
  /** Game Day: won at least once (the bonus WIN badge). */
  won?: boolean;
  /** Game Day: games lost so far (each one eases the next Game Day's CPU a notch). */
  losses?: number;
}

export interface TutorialProgress {
  version: 1;
  /** The first-launch "NEW HERE?" offer was shown (any choice sets it). */
  offered: boolean;
  /** The head the player learns with. */
  head: CharacterId;
  lessons: Partial<Record<LessonId, LessonRecord>>;
  /** Last lesson played. */
  last: LessonId | null;
  howToPlaySeen: boolean;
}

export const DEFAULT_PROGRESS: TutorialProgress = {
  version: 1,
  offered: false,
  head: 'rookie',
  lessons: {},
  last: null,
  howToPlaySeen: false,
};

/** The platform's storage (web: localStorage) at call time. */
const defaultStorage = (): KeyValueStorage | null => getPlatform().storage();

const LESSON_IDS = new Set<string>(LESSONS.map((l) => l.id));

/** Load progress (corrupt / unknown data falls back to defaults, unknown lessons are dropped). Never throws. */
export const loadProgress = (storage: KeyValueStorage | null = defaultStorage()): TutorialProgress => {
  try {
    const raw = storage?.getItem(TUTORIAL_KEY);
    if (!raw) return cloneJson(DEFAULT_PROGRESS);
    const p = JSON.parse(raw) as Partial<TutorialProgress>;
    if (p.version !== 1) return cloneJson(DEFAULT_PROGRESS);
    const lessons: TutorialProgress['lessons'] = {};
    for (const [k, v] of Object.entries(p.lessons ?? {})) {
      if (
        !LESSON_IDS.has(k) ||
        !v ||
        (v.status !== 'done' && v.status !== 'skipped' && v.status !== 'played')
      )
        continue;
      lessons[k as LessonId] = {
        // a finished Game Day is done (old saves recorded a lost one as `played`)
        status: v.status === 'played' ? 'done' : v.status,
        at: Number(v.at) || 0,
        tries: Number(v.tries) || 0,
        ...(v.won ? { won: true } : {}),
        ...(Number(v.losses) > 0 ? { losses: Math.floor(Number(v.losses)) } : {}),
      };
    }
    return {
      version: 1,
      offered: !!p.offered,
      head: CHARACTER_IDS.includes(p.head as CharacterId) ? (p.head as CharacterId) : 'rookie',
      lessons,
      last: p.last && LESSON_IDS.has(p.last) ? p.last : null,
      howToPlaySeen: !!p.howToPlaySeen,
    };
  } catch {
    return cloneJson(DEFAULT_PROGRESS);
  }
};

export const saveProgress = (
  p: TutorialProgress,
  storage: KeyValueStorage | null = defaultStorage(),
): void => {
  try {
    storage?.setItem(TUTORIAL_KEY, JSON.stringify(p));
  } catch {
    /* storage blocked: progress stays in memory */
  }
};

/**
 * The first-launch offer appears on the main menu reached from the Title (the app marks it when the player
 * presses start), never on a menu opened any other way (tests, coming back from a match).
 */
export const newHereEligible = signal(false);

/**
 * Where the app-wide copy loads from and saves to: the platform's storage, resolved ONCE (when this module is first
 * imported) so it can never load defaults from one place and overwrite the saved progress in another.
 */
const appStorage: KeyValueStorage | null = defaultStorage();

/** Live progress (the app-wide copy). */
export const progress = signal<TutorialProgress>(loadProgress(appStorage));

export const updateProgress = (patch: Partial<TutorialProgress>): void => {
  progress.value = { ...progress.value, ...patch };
  saveProgress(progress.value, appStorage);
};

/** Record a finished lesson. A lesson once DONE stays done even if a later run is skipped or lost. */
export const markLesson = (
  id: LessonId,
  status: LessonRecord['status'],
  tries: number,
  head?: CharacterId,
  extra: Pick<LessonRecord, 'won' | 'losses'> = {},
): void => {
  const cur = progress.value;
  const prev = cur.lessons[id];
  const keep = prev?.status === 'done' && status !== 'done';
  const won = !!(extra.won || prev?.won);
  const losses = extra.losses ?? prev?.losses ?? 0;
  const rec: LessonRecord = keep ? prev : { status, at: Date.now(), tries };
  progress.value = {
    ...cur,
    ...(head ? { head } : {}),
    last: id,
    lessons: {
      ...cur.lessons,
      [id]: { ...rec, ...(won ? { won: true } : {}), ...(losses > 0 ? { losses } : {}) },
    },
  };
  saveProgress(progress.value, appStorage);
};

/** CONTINUE target: the first lesson (in order) that is not done, after the last one played; null = all done. */
export const continueLesson = (p: TutorialProgress = progress.value): LessonId | null => {
  const notDone = (id: LessonId) => p.lessons[id]?.status !== 'done';
  const lastIx = p.last ? LESSONS.findIndex((l) => l.id === p.last) : -1;
  for (let i = lastIx + 1; i < LESSONS.length; i++) if (notDone(LESSONS[i]!.id)) return LESSONS[i]!.id;
  for (const l of LESSONS) if (notDone(l.id)) return l.id;
  return null;
};

/** Lessons done (✓). */
export const doneCount = (p: TutorialProgress = progress.value): number =>
  LESSONS.filter((l) => p.lessons[l.id]?.status === 'done').length;

/** Every lesson is done: the GRADUATE badge. */
export const graduated = (p: TutorialProgress = progress.value): boolean => doneCount(p) === LESSONS.length;
